// Copyright (c) 2022 by Move Closer

import { ComputedRef } from '@vue/composition-api'
import { RawLocation } from 'vue-router'

import {
  CanBeDisabled,
  CanHaveTheme,
  HasVariant,
  UseThemeClassProvides,
  UseVariantClassProvides
} from '../../../composables'

/**
 * @author Łukasz Sitnicki <lukasz.sitnicki@movecloser.pl>
 * @author Wojciech Falkowski <wojciech.falkowski@movecloser.pl> (edited)
 * @author Filip Rurak <filip.rurak@movecloser.pl>
 */
export interface AbstractLinkProps extends CanBeDisabled, CanHaveTheme, HasVariant {
  customColor?: string

  /**
   * use-link's label (text to render).
   */
  label?: string

  /**
   * Additional callback.
   */
  onClick?: (e?: Event) => void

  /**
   * Determines whether the link's target should be opened in a new browser's tab.
   */
  tabTarget?: TabOption

  /**
   * use-link's target.
   *
   * @example internal (in-app) target:
   *   target: {
   *     path: '/profile/details'
   *   }
   *
   *   target: {
   *     name: 'root.home',
   *     params: { id: 123 }
   *   }
   *
   * @example external target:
   *   target: 'https://pudelek.pl'
   */
  target: RawLocation | undefined

  /**
   * use-link's title to bind for ARIA attribute `title` in <a> tag.
   */
  title?: string
}

/**
 * @author Łukasz Sitnicki <lukasz.sitnicki@movecloser.pl>
 * @author Javlon Khalimjonov <javlon.khalimjonov@movecloser.pl> (edited)
 * @author Wojciech Falkowski <wojciech.falkowski@movecloser.pl> (edited)
 */
export interface AbstractUseLinkProvides extends UseThemeClassProvides, UseVariantClassProvides {
  /**
   * @see Link.title
   */
  titleValue: ComputedRef<string>

  /**
   * Determines whether the passed-in `link` prop has a valid `target` property.
   */
  hasCorrectTarget: ComputedRef<boolean>

  /**
   * Determines whether the link's target belongs to the app or to the external resource.
   */
  isExternal: ComputedRef<boolean>

  /**
   * Component to use if target is not a router-link
   */
  linkComponent: string
}

/**
 * @author Wojciech Falkowski <wojciech.falkowski@movecloser.pl> (edited)
 */
export enum TabOption {
  Blank = '_blank',
  Self = '_self'
}
